import { api, call, type CallFailure } from './api.js';

/**
 * THE ITEM STARTS LOADING AT THE TAP, NOT AFTER THE SCREEN MOUNTS (0.1.526).
 *
 * The maintainer, 2 Oct 2026: "When I press on an item … it briefly shows me an empty screen with
 * only a back button … and then it flashes and swaps to the item detail screen." Two waits stood in
 * series: the lazy item-detail chunk (first open of a session), THEN the `getItem` round trip, which
 * only began once that chunk had rendered. `prefetchItem` begins the read the moment the route
 * changes (app.tsx subscribes it to the router), so the two overlap; the screen takes the promise
 * once (`takePrefetchedItem`) instead of asking again.
 *
 * ⚠️ NOT A CACHE, deliberately. `cachedRead`'s header (read-cache.ts) makes "stale only on a
 * transport failure" a SECURITY property: a cache hit skips RLS after a session dies. This holds
 * nothing past the one navigation it was started for — taken once, and dropped unclaimed after
 * `TTL_MS` — and the answer is the server's, read under the live session, through the same `call`
 * (so a failure is classified and ledgered exactly as the screen's own read would be).
 */
type ItemResult = Awaited<ReturnType<typeof api.getItem.query>>;
type Outcome = { ok: true; data: ItemResult } | { ok: false; failure: CallFailure };

const TTL_MS = 5_000;
const pending = new Map<string, { promise: Promise<Outcome>; at: number }>();

export function prefetchItem(id: string): void {
  const now = Date.now();
  for (const [key, entry] of pending) if (now - entry.at > TTL_MS) pending.delete(key);
  if (pending.has(id)) return;
  pending.set(id, { promise: call('getItem', () => api.getItem.query({ id })), at: now });
}

/** The prefetched read for `id`, ONCE — a second visit asks the server again. */
export function takePrefetchedItem(id: string): Promise<Outcome> | null {
  const entry = pending.get(id);
  pending.delete(id);
  if (entry === undefined || Date.now() - entry.at > TTL_MS) return null;
  return entry.promise;
}

/** Fetch the item-detail chunk ahead of the first tap (app.tsx, once the shell is idle), so the
 *  lazy screen's Suspense fallback is not what a first open shows. */
let chunk: Promise<unknown> | null = null;
export function preloadItemDetailChunk(): void {
  chunk ??= import('../screens/item-detail.js').catch(() => {
    // A failed preload is not a failure: the route's own lazy import retries and reports.
    chunk = null;
  });
}
